import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getOptimizedUrl, isCloudinaryUrl } from '../lib/cloudinary';

/**
 * PhotoLightbox Component
 * 
 * A full-featured lightbox with:
 * - Progressive loading (medium -> full)
 * - Keyboard navigation (left/right/escape)
 * - Touch swipe support for mobile
 * - Download option
 * - Photo counter
 * - Smooth transitions
 */
const PhotoLightbox = ({
  isOpen,
  photos = [],
  currentIndex = 0,
  onClose,
  onNavigate,
  albumTitle = 'Album',
}) => {
  const [loadedQuality, setLoadedQuality] = useState('medium');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);

  const currentPhoto = photos[currentIndex];
  const hasNext = currentIndex < photos.length - 1;
  const hasPrev = currentIndex > 0;

  // Reset state when photo changes
  useEffect(() => {
    if (isOpen && currentPhoto) {
      setLoadedQuality('medium');
      setIsImageLoaded(false);
    }
  }, [isOpen, currentIndex, currentPhoto]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasPrev) onNavigate(currentIndex - 1);
          break;
        case 'ArrowRight':
          if (hasNext) onNavigate(currentIndex + 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, hasNext, hasPrev, onClose, onNavigate]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isOpen]);

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && hasNext) {
      onNavigate(currentIndex + 1);
    } else if (isRightSwipe && hasPrev) {
      onNavigate(currentIndex - 1);
    }
  };

  // Load full quality after medium is loaded
  const handleMediumLoaded = useCallback(() => {
    setIsImageLoaded(true);
    // Preload full quality
    if (isCloudinaryUrl(currentPhoto?.image_url)) {
      const fullUrl = getOptimizedUrl(currentPhoto.image_url, 'full', 'default');
      const img = new Image();
      img.onload = () => setLoadedQuality('full');
      img.src = fullUrl;
    }
  }, [currentPhoto]);

  const handleDownload = useCallback(() => {
    if (!currentPhoto) return;
    const url = getOptimizedUrl(currentPhoto.image_url, 'full', 'default');
    window.open(url, '_blank');
  }, [currentPhoto]);

  if (!isOpen || !currentPhoto) return null;

  const mediumUrl = getOptimizedUrl(currentPhoto.image_url, 'medium', 'desktop');
  const fullUrl = getOptimizedUrl(currentPhoto.image_url, 'full', 'default');
  const displayUrl = loadedQuality === 'full' ? fullUrl : mediumUrl;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label={`Foto ${currentIndex + 1} van ${photos.length}`}
      tabIndex={-1}
      data-testid="photo-lightbox"
    >
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
        aria-label="Sluit lightbox"
        data-testid="lightbox-close"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Photo counter */}
      <div className="absolute top-4 left-4 z-10 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Download button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        className="absolute top-4 right-20 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
        aria-label="Download foto"
        data-testid="lightbox-download"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </button>

      {/* Previous button */}
      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors hidden sm:block"
          aria-label="Vorige foto"
          data-testid="lightbox-prev"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next button */}
      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors hidden sm:block"
          aria-label="Volgende foto"
          data-testid="lightbox-next"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Main image container */}
      <div 
        className="max-w-6xl w-full px-4 sm:px-12"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading spinner */}
        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Image */}
        <img
          src={displayUrl}
          alt={currentPhoto.caption || `Foto ${currentIndex + 1} van ${albumTitle}`}
          className={`max-w-full max-h-[80vh] object-contain mx-auto rounded-lg shadow-2xl transition-opacity duration-300 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleMediumLoaded}
          data-testid="lightbox-image"
        />

        {/* Caption */}
        {currentPhoto.caption && (
          <p className="text-white text-center mt-4 text-lg max-w-2xl mx-auto">
            {currentPhoto.caption}
          </p>
        )}

        {/* Quality indicator */}
        {isImageLoaded && loadedQuality === 'medium' && (
          <p className="text-white/50 text-center mt-2 text-xs">
            Hoge kwaliteit aan het laden...
          </p>
        )}
      </div>

      {/* Swipe hint for mobile */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-white/50 text-sm sm:hidden">
        ← Swipe om te navigeren →
      </div>
    </div>
  );
};

export default PhotoLightbox;
